/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Derives a single, analytics-friendly OS-version string from the user agent.
 *
 * The value is a hyphen-joined token rather than free text so it can be
 * grouped as-is in reporting:
 *
 * | Platform             | Example value            |
 * | -------------------- | ------------------------ |
 * | Samsung Tizen        | `Tizen-7.0`              |
 * | LG webOS             | `webOS-23`               |
 * | Amazon Vega OS       | `Kepler-1.2`             |
 * | Macintosh            | `Macintosh-Chrome-153.0.0.0` |
 * | Windows              | `Windows-Edge-130.0.0.0` |
 *
 * On TV platforms the OS itself is versioned, so the browser is left out. On
 * desktop the OS version in the UA is frozen (`Windows NT 10.0`,
 * `Mac OS X 10_15_7`) and tells us nothing, so the browser and its version
 * carry the signal instead.
 */

/** Returned when no rule below matches the user agent. */
const UNKNOWN = "Unknown";

/**
 * LG does not put the webOS version in the user agent — the only version it
 * carries is Chromium's, and each webOS release ships one fixed Chromium
 * major. This maps that Chromium major back to the webOS release.
 *
 * Unlisted majors fall through to `webOS-Unknown`; add the pair here when LG
 * ships a new release rather than guessing from the nearest neighbour.
 */
const WEBOS_VERSION_BY_CHROME_MAJOR: Record<string, string> = {
  "38": "4.0",
  "53": "4.5",
  "68": "5.0",
  "79": "6.0",
  "87": "22",
  "94": "23",
  "108": "24",
  "120": "25",
  "132": "26"
};

/** Capture group 1 of `pattern` in `ua`, or undefined when it does not match. */
function match(ua: string, pattern: RegExp): string | undefined {
  const found = ua.match(pattern);
  return found === null || !found[1] ? undefined : found[1];
}

/**
 * Browser name and version, e.g. `Chrome-153.0.0.0`.
 *
 * Order matters: the Edge UA also contains `Chrome/`, and Chrome and Edge UAs
 * both end in `Safari/`. Safari's own version lives in `Version/`, not in the
 * `Safari/` build number.
 */
function getBrowser(ua: string): string | undefined {
  const edge = match(ua, /Edg\/(\d+(?:\.\d+)*)/);
  if (edge !== undefined) return `Edge-${edge}`;

  const chrome = match(ua, /Chrome\/(\d+(?:\.\d+)*)/);
  if (chrome !== undefined) return `Chrome-${chrome}`;

  const firefox = match(ua, /Firefox\/(\d+(?:\.\d+)*)/);
  if (firefox !== undefined) return `Firefox-${firefox}`;

  if (/Safari\//.test(ua)) {
    const safari = match(ua, /Version\/(\d+(?:\.\d+)*)/);
    return `Safari-${safari === undefined ? UNKNOWN : safari}`;
  }

  return undefined;
}

/**
 * Returns the OS version token for `userAgent`, defaulting to the current
 * browser's user agent. Never throws and never returns an empty string: an
 * unrecognised user agent yields `"Unknown"`.
 */
export function getOSVersion(userAgent?: string): string {
  const currentUa = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const ua = userAgent === undefined ? currentUa : userAgent;

  if (ua === "") return UNKNOWN;

  // TV platforms first: their UAs also contain "Linux", and the Vega OS UA
  // additionally contains "Chrome/", so a desktop rule would swallow them.
  const tizen = match(ua, /Tizen[\s/]?(\d+(?:\.\d+)*)/i);
  if (tizen !== undefined) return `Tizen-${tizen}`;
  if (/Tizen/i.test(ua)) return `Tizen-${UNKNOWN}`;

  // Amazon Vega OS reports its Kepler platform version.
  const kepler = match(ua, /Kepler[\s/]?(\d+(?:\.\d+)*)/i);
  if (kepler !== undefined) return `Kepler-${kepler}`;

  // "Web0S" (with a zero) is what LG actually ships; accept "webOS" too.
  if (/Web0S|webOS/i.test(ua)) {
    const chromeMajor = match(ua, /Chrome\/(\d+)/);
    const webOsVersion =
      chromeMajor === undefined
        ? undefined
        : WEBOS_VERSION_BY_CHROME_MAJOR[chromeMajor];
    return `webOS-${webOsVersion === undefined ? UNKNOWN : webOsVersion}`;
  }

  const browser = getBrowser(ua);
  if (browser === undefined) return UNKNOWN;

  if (/Macintosh|Mac OS X/.test(ua)) return `Macintosh-${browser}`;
  if (/Windows/.test(ua)) return `Windows-${browser}`;

  return UNKNOWN;
}
